import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    CircularProgress,
    Alert,
    LinearProgress,
    Collapse,
    IconButton,
} from '@mui/material';
import {
    CloudUpload,
    CheckCircle,
    Error as ErrorIcon,
    AutoAwesome,
    Add,
    ExpandLess,
    Close,
    PhotoLibrary,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

interface Trip {
    tripId: string;
    title: string;
    destination: string;
    startDate?: string;
    endDate?: string;
}

interface SelectedFile {
    file: File;
    preview: string;
}

interface UploadingFile {
    file: File;
    progress: number;
    status: 'pending' | 'uploading' | 'success' | 'error';
    error?: string;
}

interface MediaUploadDialogProps {
    open: boolean;
    onClose: () => void;
    tripId?: string;  // If provided, skip trip selection and upload immediately
    tripTitle?: string;  // Display name when tripId is provided
    onUploadComplete?: () => void;
}

const MediaUploadDialog: React.FC<MediaUploadDialogProps> = ({
    open,
    onClose,
    tripId,
    tripTitle,
    onUploadComplete,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    // Trip selection state (only used when tripId not provided)
    const [trips, setTrips] = useState<Trip[]>([]);
    const [selectedTripId, setSelectedTripId] = useState<string>('');
    const [loadingTrips, setLoadingTrips] = useState(false);
    
    // Create trip state
    const [showCreateTrip, setShowCreateTrip] = useState(false);
    const [newTripName, setNewTripName] = useState('');
    const [newTripDestination, setNewTripDestination] = useState('');
    const [newTripStartDate, setNewTripStartDate] = useState('');
    const [newTripEndDate, setNewTripEndDate] = useState('');
    const [creatingTrip, setCreatingTrip] = useState(false);
    
    // Selected files (before upload)
    const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
    
    // Upload state
    const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    // Determine the effective tripId
    const effectiveTripId = tripId || selectedTripId;
    
    // Mode: immediate upload (when tripId provided) vs manual upload (select photos first)
    const isImmediateMode = !!tripId;

    // Fetch trips when dialog opens (only if no tripId provided)
    useEffect(() => {
        if (open && !tripId) {
            fetchTrips();
        }
    }, [open, tripId]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setSelectedTripId('');
            setShowCreateTrip(false);
            setNewTripName('');
            setNewTripDestination('');
            setNewTripStartDate('');
            setNewTripEndDate('');
            setSelectedFiles([]);
            setUploadingFiles([]);
            setError(null);
            setIsUploading(false);
        }
    }, [open]);

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            selectedFiles.forEach(f => URL.revokeObjectURL(f.preview));
        };
    }, [selectedFiles]);

    // Auto-close after all uploads complete successfully
    useEffect(() => {
        if (uploadingFiles.length > 0 && 
            uploadingFiles.every(f => f.status === 'success' || f.status === 'error')) {
            const hasSuccess = uploadingFiles.some(f => f.status === 'success');
            if (hasSuccess) {
                // Small delay to show completion state
                const timer = setTimeout(() => {
                    onUploadComplete?.();
                    onClose();
                }, 1000);
                return () => clearTimeout(timer);
            }
        }
    }, [uploadingFiles, onClose, onUploadComplete]);

    const fetchTrips = async () => {
        setLoadingTrips(true);
        try {
            const idToken = localStorage.getItem('idToken');
            const response = await fetch(`${API_URL}/trips`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                setTrips(data.trips || []);
            }
        } catch (err) {
            console.error('Error fetching trips:', err);
        } finally {
            setLoadingTrips(false);
        }
    };

    const handleCreateTrip = async () => {
        if (!newTripName.trim() || !newTripDestination.trim()) {
            setError('Trip name and destination are required');
            return;
        }
        
        setCreatingTrip(true);
        setError(null);
        
        try {
            const idToken = localStorage.getItem('idToken');
            const response = await fetch(`${API_URL}/trips`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    title: newTripName.trim(),
                    destination: newTripDestination.trim(),
                    startDate: newTripStartDate || undefined,
                    endDate: newTripEndDate || undefined,
                    status: 'planning',
                    visibility: 'private',
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                const newTrip = data.trip;
                setTrips(prev => [newTrip, ...prev]);
                setSelectedTripId(newTrip.tripId);
                setShowCreateTrip(false);
                setNewTripName('');
                setNewTripDestination('');
                setNewTripStartDate('');
                setNewTripEndDate('');
            } else {
                setError('Failed to create trip');
            }
        } catch (err) {
            console.error('Error creating trip:', err);
            setError('Failed to create trip');
        } finally {
            setCreatingTrip(false);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        
        if (isImmediateMode) {
            // Immediate mode: start uploading right away
            const newFiles: UploadingFile[] = Array.from(files).map(file => ({
                file,
                progress: 0,
                status: 'pending' as const,
            }));
            setUploadingFiles(prev => [...prev, ...newFiles]);
        } else {
            // Manual mode: add to selected files with preview
            const newSelectedFiles: SelectedFile[] = Array.from(files).map(file => ({
                file,
                preview: URL.createObjectURL(file),
            }));
            setSelectedFiles(prev => [...prev, ...newSelectedFiles]);
        }
        
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveSelectedFile = (index: number) => {
        setSelectedFiles(prev => {
            const removed = prev[index];
            if (removed) {
                URL.revokeObjectURL(removed.preview);
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    const uploadSingleFile = async (file: File, index: number, targetTripId: string) => {
        setUploadingFiles(prev => prev.map((f, i) => 
            i === index ? { ...f, status: 'uploading' } : f
        ));

        try {
            const idToken = localStorage.getItem('idToken');

            // Get presigned URL
            const presignResponse = await fetch(`${API_URL}/trips/${targetTripId}/photos/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    fileName: file.name,
                    fileType: file.type,
                })
            });

            if (!presignResponse.ok) throw new Error('Failed to get upload URL');

            const { presignedUrl, photoMetadata } = await presignResponse.json();

            // Upload to S3
            const xhr = new XMLHttpRequest();
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    const progress = Math.round((e.loaded / e.total) * 100);
                    setUploadingFiles(prev => prev.map((f, i) => 
                        i === index ? { ...f, progress } : f
                    ));
                }
            };

            await new Promise<void>((resolve, reject) => {
                xhr.onload = () => xhr.status === 200 ? resolve() : reject(new Error('Upload failed'));
                xhr.onerror = () => reject(new Error('Upload failed'));
                xhr.open('PUT', presignedUrl);
                xhr.setRequestHeader('Content-Type', file.type);
                xhr.send(file);
            });

            // Confirm upload
            await fetch(`${API_URL}/trips/${targetTripId}/photos/confirm`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ photoMetadata })
            });

            setUploadingFiles(prev => prev.map((f, i) => 
                i === index ? { ...f, status: 'success', progress: 100 } : f
            ));
        } catch (err) {
            console.error('Upload error:', err);
            setUploadingFiles(prev => prev.map((f, i) => 
                i === index ? { ...f, status: 'error', error: 'Upload failed' } : f
            ));
        }
    };

    // For immediate mode: upload when files are added
    const uploadFile = useCallback(async (index: number) => {
        const file = uploadingFiles[index]?.file;
        if (!file || !effectiveTripId) return;
        await uploadSingleFile(file, index, effectiveTripId);
    }, [effectiveTripId, uploadingFiles]);

    // Immediate mode: auto-upload pending files
    useEffect(() => {
        if (isImmediateMode) {
            uploadingFiles.forEach((file, index) => {
                if (file.status === 'pending' && effectiveTripId) {
                    uploadFile(index);
                }
            });
        }
    }, [uploadingFiles, effectiveTripId, uploadFile, isImmediateMode]);

    // Manual upload button handler
    const handleStartUpload = async () => {
        if (!effectiveTripId || selectedFiles.length === 0) return;
        
        setIsUploading(true);
        
        // Convert selected files to uploading files
        const filesToUpload: UploadingFile[] = selectedFiles.map(sf => ({
            file: sf.file,
            progress: 0,
            status: 'pending' as const,
        }));
        
        setUploadingFiles(filesToUpload);
        
        // Clear selected files (previews)
        selectedFiles.forEach(f => URL.revokeObjectURL(f.preview));
        setSelectedFiles([]);
        
        // Upload each file
        for (let i = 0; i < filesToUpload.length; i++) {
            await uploadSingleFile(filesToUpload[i].file, i, effectiveTripId);
        }
        
        setIsUploading(false);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            if (isImmediateMode) {
                const newFiles: UploadingFile[] = Array.from(files).map(file => ({
                    file,
                    progress: 0,
                    status: 'pending' as const,
                }));
                setUploadingFiles(prev => [...prev, ...newFiles]);
            } else {
                const newSelectedFiles: SelectedFile[] = Array.from(files).map(file => ({
                    file,
                    preview: URL.createObjectURL(file),
                }));
                setSelectedFiles(prev => [...prev, ...newSelectedFiles]);
            }
        }
    };

    const handleClose = () => {
        if (uploadingFiles.some(f => f.status === 'success')) {
            onUploadComplete?.();
        }
        onClose();
    };

    const canStartUpload = !!effectiveTripId && selectedFiles.length > 0 && !isUploading;
    const hasActiveUploads = uploadingFiles.some(f => f.status === 'uploading' || f.status === 'pending');
    const allUploadsComplete = uploadingFiles.length > 0 && uploadingFiles.every(f => f.status === 'success' || f.status === 'error');

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 600 }}>
                {tripId ? `Upload to ${tripTitle || 'Trip'}` : 'Upload Media'}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*,video/*"
                    multiple
                    style={{ display: 'none' }}
                />

                {/* Upload Area */}
                <Box
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#2D6E6A', bgcolor: '#f0faf9' }
                    }}
                    onClick={handleUploadClick}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <CloudUpload sx={{ fontSize: 48, color: '#2D6E6A', mb: 1 }} />
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                        Click to select photos & videos
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        or drag and drop files here
                    </Typography>
                </Box>

                {/* Selected Files Preview (manual mode only) */}
                {!isImmediateMode && selectedFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhotoLibrary sx={{ fontSize: 18 }} />
                            {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 1,
                            maxHeight: 120,
                            overflowY: 'auto',
                            p: 1,
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                        }}>
                            {selectedFiles.map((sf, index) => (
                                <Box 
                                    key={index} 
                                    sx={{ 
                                        position: 'relative',
                                        width: 60,
                                        height: 60,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        border: '1px solid #ddd',
                                    }}
                                >
                                    {sf.file.type.startsWith('video/') ? (
                                        <Box sx={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            bgcolor: '#333',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                            <Typography variant="caption" sx={{ color: 'white', fontSize: '0.6rem' }}>
                                                Video
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <img 
                                            src={sf.preview} 
                                            alt={sf.file.name}
                                            style={{ 
                                                width: '100%', 
                                                height: '100%', 
                                                objectFit: 'cover' 
                                            }}
                                        />
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveSelectedFile(index)}
                                        sx={{
                                            position: 'absolute',
                                            top: 1,
                                            right: 1,
                                            bgcolor: 'rgba(0,0,0,0.5)',
                                            color: 'white',
                                            p: 0.2,
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                        }}
                                    >
                                        <Close sx={{ fontSize: 12 }} />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Trip Selection (only when tripId not provided) - Simplified */}
                {!tripId && (
                    <Box sx={{ mt: 2 }}>
                        {loadingTrips ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Select Trip</InputLabel>
                                    <Select
                                        value={selectedTripId}
                                        label="Select Trip"
                                        onChange={(e) => setSelectedTripId(e.target.value)}
                                    >
                                        {trips.map(trip => (
                                            <MenuItem key={trip.tripId} value={trip.tripId}>
                                                {trip.title} - {trip.destination}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Create Trip Toggle */}
                                <Button
                                    size="small"
                                    startIcon={showCreateTrip ? <ExpandLess /> : <Add />}
                                    onClick={() => setShowCreateTrip(!showCreateTrip)}
                                    sx={{ textTransform: 'none', color: '#2D6E6A', mt: 1 }}
                                >
                                    {showCreateTrip ? 'Cancel' : 'Create New Trip'}
                                </Button>

                                {/* Create Trip Form */}
                                <Collapse in={showCreateTrip}>
                                    <Box sx={{ mt: 1.5, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                        <TextField
                                            fullWidth
                                            label="Trip Name *"
                                            value={newTripName}
                                            onChange={(e) => setNewTripName(e.target.value)}
                                            sx={{ mb: 1.5 }}
                                            size="small"
                                        />
                                        <TextField
                                            fullWidth
                                            label="Destination *"
                                            value={newTripDestination}
                                            onChange={(e) => setNewTripDestination(e.target.value)}
                                            sx={{ mb: 1.5 }}
                                            size="small"
                                        />
                                        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                                            <TextField
                                                fullWidth
                                                label="Start Date"
                                                type="date"
                                                value={newTripStartDate}
                                                onChange={(e) => setNewTripStartDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                size="small"
                                            />
                                            <TextField
                                                fullWidth
                                                label="End Date"
                                                type="date"
                                                value={newTripEndDate}
                                                onChange={(e) => setNewTripEndDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                size="small"
                                            />
                                        </Box>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleCreateTrip}
                                            disabled={creatingTrip || !newTripName.trim() || !newTripDestination.trim()}
                                            sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
                                        >
                                            {creatingTrip ? <CircularProgress size={18} /> : 'Create Trip'}
                                        </Button>
                                    </Box>
                                </Collapse>
                            </>
                        )}
                    </Box>
                )}

                {/* Upload Progress */}
                {uploadingFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            {allUploadsComplete ? '✓ Upload Complete!' : `Uploading ${uploadingFiles.length} file${uploadingFiles.length !== 1 ? 's' : ''}...`}
                        </Typography>
                        {uploadingFiles.map((file, index) => (
                            <Box key={index} sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    {file.status === 'success' && <CheckCircle sx={{ color: '#4CAF50', fontSize: 16 }} />}
                                    {file.status === 'error' && <ErrorIcon sx={{ color: '#f44336', fontSize: 16 }} />}
                                    {(file.status === 'pending' || file.status === 'uploading') && (
                                        <CircularProgress size={14} sx={{ color: '#2D6E6A' }} />
                                    )}
                                    <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.8rem' }}>
                                        {file.file.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {file.status === 'success' ? 'Done' :
                                         file.status === 'error' ? 'Failed' :
                                         `${file.progress}%`}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={file.progress}
                                    sx={{
                                        height: 4,
                                        borderRadius: 2,
                                        bgcolor: file.status === 'error' ? '#ffebee' : '#e0f2f1',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: file.status === 'error' ? '#f44336' :
                                                     file.status === 'success' ? '#4CAF50' : '#2D6E6A'
                                        }
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                )}

                {/* AI Info - only show before upload starts */}
                {uploadingFiles.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2, borderRadius: '8px', py: 0.5 }} icon={<AutoAwesome sx={{ fontSize: 18 }} />}>
                        <Typography variant="body2">AI will automatically tag and caption your photos</Typography>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button
                    onClick={handleClose}
                    sx={{ textTransform: 'none' }}
                    disabled={hasActiveUploads}
                >
                    {allUploadsComplete ? 'Done' : 'Cancel'}
                </Button>
                {/* Upload button for manual mode */}
                {!isImmediateMode && !allUploadsComplete && (
                    <Button
                        variant="contained"
                        onClick={handleStartUpload}
                        disabled={!canStartUpload}
                        startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
                        sx={{ 
                            bgcolor: '#2D6E6A', 
                            '&:hover': { bgcolor: '#009688' },
                            textTransform: 'none',
                        }}
                    >
                        {isUploading ? 'Uploading...' : `Upload${selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ''}`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default MediaUploadDialog;
